import { useCallback, useEffect, useMemo, useState } from "react";
import useApi from "../api/api";
import { sameRecipient } from "../utils/recipients";

/**
 * The sender's saved (favorite) and recent recipients, plus save / rename / remove.
 *
 * `people` is the merged list the strip and People screen show: favorites first (alphabetical),
 * then recents that aren't already favorites (newest first). Each entry is
 * `{ type, value, nickname?, uuid?, last_sent_at?, saved }`.
 */
const useRecipients = () => {
  const api = useApi();
  const [saved, setSaved] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [savedRes, recentRes] = await Promise.all([
        api.get("recipients/saved"),
        api.get("recipients/recent"),
      ]);
      setSaved(savedRes.data);
      setRecent(recentRes.data);
    } catch (err) {
      // A convenience only; every screen works without it.
      console.warn("could not load recipients", err);
    } finally {
      setLoaded(true);
    }
  }, [api]);

  useEffect(() => {
    reload();
  }, [reload]);

  const people = useMemo(() => {
    const favorites = saved.map((s) => {
      const lastSent = recent.find((r) => sameRecipient(s, r.value));
      return { ...s, saved: true, last_sent_at: lastSent?.last_sent_at };
    });
    const others = recent
      .filter((r) => !saved.some((s) => sameRecipient(s, r.value)))
      .map((r) => ({ ...r, saved: false }));
    return [...favorites, ...others];
  }, [saved, recent]);

  const findSaved = useCallback(
    (value) => saved.find((s) => sameRecipient(s, value)),
    [saved]
  );

  /** Saves (or renames) a recipient. `recipient` is `{ type, value }` in stored form. */
  const save = useCallback(
    async (recipient, nickname) => {
      await api.post("recipients/saved", { ...recipient, nickname: nickname.trim() });
      await reload();
    },
    [api, reload]
  );

  const remove = useCallback(
    async (uuid) => {
      await api.delete(`recipients/saved/${uuid}`);
      await reload();
    },
    [api, reload]
  );

  return { people, saved, recent, loaded, findSaved, save, remove, reload };
};

export default useRecipients;
